import { useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import { SERVER_URL } from '../utils/serverConfig';

// Create axios instance with base configuration
const createApiInstance = () => {
  const instance = axios.create({
    baseURL: SERVER_URL,
    timeout: 120000, // Increased to 2 minutes for complex queries
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Request interceptor
  instance.interceptors.request.use(
    (config) => {
      // Add auth token if available
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      
      // Log request in development
      if (process.env.NODE_ENV === 'development') {
        console.log(`🚀 API Request: ${config.method?.toUpperCase()} ${config.url}`);
      }
      
      return config;
    },
    (error) => {
      console.error('❌ Request Error:', error);
      return Promise.reject(error);
    }
  );

  // Response interceptor
  instance.interceptors.response.use(
    (response) => {
      // Log response in development
      if (process.env.NODE_ENV === 'development') {
        console.log(`✅ API Response: ${response.config.method?.toUpperCase()} ${response.config.url}`, response.status);
      }
      return response;
    },
    (error) => {
      // Handle common errors
      if (error.response?.status === 401) {
        // Only redirect to login if:
        // 1. We're NOT on the login page already
        // 2. This is NOT a login API call
        // 3. We're NOT on a public route
        // 4. This is NOT the password update call (wrong current password = 401, show error instead of logout)
        const currentPath = window.location.pathname;
        const isLoginCall = error.config?.url?.includes('/auth/login');
        const isPasswordUpdateCall =
          (error.config?.method === 'put' || error.config?.method === 'PUT') &&
          error.config?.url?.includes('/auth/password');

        // List of public routes that shouldn't redirect on 401
        const publicRoutes = [
          '/',
          '/login',
          '/register',
          '/forgot-password',
          '/reset-password',
          '/verify-email',
          '/invite',
          '/privacy-policy',
          '/terms-of-service',
          '/security',
          '/download',
          '/book-demo',
          '/contact-us',
          '/thanks-contact-us',
          '/thanks-demo-request',
          '/features',
          '/pricing',
          '/blog',
          '/landing-alt',
          '/alternate',
          '/ai-workforce-intelligence',
        ];
        
        // Check if current path is a public route or starts with a public route path
        const isPublicRoute = publicRoutes.some(route => 
          currentPath === route || 
          currentPath.startsWith(route + '/') ||
          currentPath.startsWith('/features/') ||
          currentPath.startsWith('/solutions/') ||
          currentPath.startsWith('/blog/') ||
          currentPath.startsWith('/compare/')
        );
        
        if (!isLoginCall && !isPublicRoute && !isPasswordUpdateCall) {
          // Token expired or invalid — redirect to login. window.location.href
          // is a full reload which wipes in-memory query caches by side-effect,
          // but we still purge user-scoped localStorage explicitly so the
          // login page doesn't render with the previous user's org metadata.
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('organization');
          localStorage.removeItem('isMainAdminSwitch');
          localStorage.removeItem('mainAdminSwitchUserId');
          localStorage.removeItem('crm.selectedProjectId');
          try { sessionStorage.removeItem('currency'); } catch (_e) {}
          try { sessionStorage.removeItem('crm-meet-connect-project'); } catch (_e) {}
          window.location.href = '/login';
        }
      }
      
      // Log error in development
      if (process.env.NODE_ENV === 'development') {
        console.error(`❌ API Error: ${error.config?.method?.toUpperCase()} ${error.config?.url}`, error.response?.status, error.response?.data);
      }
      
      return Promise.reject(error);
    }
  );

  return instance;
};

// Custom hook for API calls
export const useApi = () => {
  const apiInstance = useRef(createApiInstance());

  // Update token when it changes
  const updateToken = useCallback((token) => {
    if (token) {
      apiInstance.current.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete apiInstance.current.defaults.headers.common['Authorization'];
    }
  }, []);

  // API methods - wrap in useMemo to return stable reference
  const api = useMemo(() => ({
    get: (url, config = {}) => apiInstance.current.get(url, config),
    post: (url, data = {}, config = {}) => apiInstance.current.post(url, data, config),
    put: (url, data = {}, config = {}) => apiInstance.current.put(url, data, config),
    patch: (url, data = {}, config = {}) => apiInstance.current.patch(url, data, config),
    delete: (url, config = {}) => apiInstance.current.delete(url, config),
    updateToken,
  }), [updateToken]);

  return api;
};

// Export the axios instance for direct use if needed
export const apiInstance = createApiInstance();
export default useApi;
